/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CancelRunPathParameters,
  GetProposedSourcePathParameters,
  GetRunPathParameters,
  GetRunProposedSourceForObjectPathParameters,
  ListRunProposedSourcesParams,
  ListRunProposedSourcesPathParameters,
  ListRunsForRemedyParams,
  ListRunsForRemedyPathParameters,
  RetryRunPathParameters,
  RollbackAgenticRunPathParameters,
  TLAgenticRemediationProposedSource,
  TLAgenticRemediationRunDetail,
  TLAgenticRemediationRunRequest,
  TLAgenticRemediationRunsListResponse,
  TLAgenticRemediationRunStartedResponse,
  TLAgenticRollbackRequest,
  TLAgenticRollbackResponse,
  TLAgenticRunProposedSourceContent,
  TLAgenticRunProposedSourcesListResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Start an agentic remediation workflow for one or more objects.
 *
 * Returns the pre-created `run_ids` immediately; status is observable via
 * GET ``/remediation-runs/{run_id}``. Objects with an unsupported type
 * (anything but CLAS/FUGR/INTF/PROG) get no run and are returned in
 * ``skipped_objects``.
 * @summary Start Runs
 */
export const startRuns = (
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRemediationRunStartedResponse>(
    {
      url: `/remediation-runs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAgenticRemediationRunRequest,
      signal,
    },
    options,
  );
};

export const getStartRunsQueryKey = (tLAgenticRemediationRunRequest?: BodyType<TLAgenticRemediationRunRequest>) => {
  return ['POST', 'remediation-runs', tLAgenticRemediationRunRequest] as const;
};

export const getStartRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof startRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStartRunsQueryKey(tLAgenticRemediationRunRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof startRuns>>> = ({ signal }) =>
    startRuns(tLAgenticRemediationRunRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof startRuns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StartRunsQueryResult = NonNullable<Awaited<ReturnType<typeof startRuns>>>;
export type StartRunsQueryError = ErrorType<TLHTTPValidationError>;

export function useStartRuns<TData = Awaited<ReturnType<typeof startRuns>>, TError = ErrorType<TLHTTPValidationError>>(
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof startRuns>>, TError, Awaited<ReturnType<typeof startRuns>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartRuns<TData = Awaited<ReturnType<typeof startRuns>>, TError = ErrorType<TLHTTPValidationError>>(
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof startRuns>>,
          TError,
          Awaited<ReturnType<typeof startRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartRuns<TData = Awaited<ReturnType<typeof startRuns>>, TError = ErrorType<TLHTTPValidationError>>(
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start Runs
 */

export function useStartRuns<TData = Awaited<ReturnType<typeof startRuns>>, TError = ErrorType<TLHTTPValidationError>>(
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStartRunsQueryOptions(tLAgenticRemediationRunRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Start Runs
 */
export const prefetchStartRunsQuery = async <
  TData = Awaited<ReturnType<typeof startRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStartRunsQueryOptions(tLAgenticRemediationRunRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Start Runs
 */
export const invalidateStartRuns = async (
  queryClient: QueryClient,
  tLAgenticRemediationRunRequest: BodyType<TLAgenticRemediationRunRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getStartRunsQueryKey(tLAgenticRemediationRunRequest) }, options);

  return queryClient;
};

/**
 * @summary List Runs For Remedy
 */
export const listRunsForRemedy = (
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRemediationRunsListResponse>(
    { url: `/remediation-runs/${remedyId}/runs`, method: 'GET', params, signal },
    options,
  );
};

export const getListRunsForRemedyQueryKey = (
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
) => {
  return ['remediation-runs', remedyId, 'runs', ...(params ? [params] : [])] as const;
};

export const getListRunsForRemedyQueryOptions = <
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRunsForRemedyQueryKey({ remedyId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRunsForRemedy>>> = ({ signal }) =>
    listRunsForRemedy({ remedyId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListRunsForRemedyQueryResult = NonNullable<Awaited<ReturnType<typeof listRunsForRemedy>>>;
export type ListRunsForRemedyQueryError = ErrorType<TLHTTPValidationError>;

export function useListRunsForRemedy<
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunsForRemedyPathParameters,
  params: undefined | ListRunsForRemedyParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunsForRemedy>>,
          TError,
          Awaited<ReturnType<typeof listRunsForRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRunsForRemedy<
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunsForRemedy>>,
          TError,
          Awaited<ReturnType<typeof listRunsForRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRunsForRemedy<
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Runs For Remedy
 */

export function useListRunsForRemedy<
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListRunsForRemedyQueryOptions({ remedyId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Runs For Remedy
 */
export const prefetchListRunsForRemedyQuery = async <
  TData = Awaited<ReturnType<typeof listRunsForRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunsForRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListRunsForRemedyQueryOptions({ remedyId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Runs For Remedy
 */
export const invalidateListRunsForRemedy = async (
  queryClient: QueryClient,
  { remedyId }: ListRunsForRemedyPathParameters,
  params?: ListRunsForRemedyParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListRunsForRemedyQueryKey({ remedyId }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Run
 */
export const getRun = (
  { runId }: GetRunPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRemediationRunDetail>(
    { url: `/remediation-runs/${runId}`, method: 'GET', signal },
    options,
  );
};

export const getGetRunQueryKey = ({ runId }: GetRunPathParameters) => {
  return ['remediation-runs', runId] as const;
};

export const getGetRunQueryOptions = <
  TData = Awaited<ReturnType<typeof getRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: GetRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRunQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRun>>> = ({ signal }) =>
    getRun({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRunQueryResult = NonNullable<Awaited<ReturnType<typeof getRun>>>;
export type GetRunQueryError = ErrorType<TLHTTPValidationError>;

export function useGetRun<TData = Awaited<ReturnType<typeof getRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetRunPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getRun>>, TError, Awaited<ReturnType<typeof getRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRun<TData = Awaited<ReturnType<typeof getRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getRun>>, TError, Awaited<ReturnType<typeof getRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRun<TData = Awaited<ReturnType<typeof getRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Run
 */

export function useGetRun<TData = Awaited<ReturnType<typeof getRun>>, TError = ErrorType<TLHTTPValidationError>>(
  { runId }: GetRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRunQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Run
 */
export const prefetchGetRunQuery = async <
  TData = Awaited<ReturnType<typeof getRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: GetRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRunQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Run
 */
export const invalidateGetRun = async (
  queryClient: QueryClient,
  { runId }: GetRunPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetRunQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * Cooperatively cancel a QUEUED or RUNNING agentic run.
 *
 * A QUEUED row never starts; a RUNNING agent loop exits on its next
 * between-turn poll. Terminal runs cannot be cancelled.
 * @summary Cancel Run
 */
export const cancelRun = (
  { runId }: CancelRunPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/remediation-runs/${runId}/cancel`, method: 'POST', signal }, options);
};

export const getCancelRunQueryKey = ({ runId }: CancelRunPathParameters) => {
  return ['POST', 'remediation-runs', runId, 'cancel'] as const;
};

export const getCancelRunQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: CancelRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelRunQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelRun>>> = ({ signal }) =>
    cancelRun({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof cancelRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CancelRunQueryResult = NonNullable<Awaited<ReturnType<typeof cancelRun>>>;
export type CancelRunQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelRun<TData = Awaited<ReturnType<typeof cancelRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: CancelRunPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof cancelRun>>, TError, Awaited<ReturnType<typeof cancelRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelRun<TData = Awaited<ReturnType<typeof cancelRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: CancelRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelRun>>,
          TError,
          Awaited<ReturnType<typeof cancelRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelRun<TData = Awaited<ReturnType<typeof cancelRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: CancelRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Run
 */

export function useCancelRun<TData = Awaited<ReturnType<typeof cancelRun>>, TError = ErrorType<TLHTTPValidationError>>(
  { runId }: CancelRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelRunQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Run
 */
export const prefetchCancelRunQuery = async <
  TData = Awaited<ReturnType<typeof cancelRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: CancelRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelRunQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Run
 */
export const invalidateCancelRun = async (
  queryClient: QueryClient,
  { runId }: CancelRunPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelRunQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * Return the run's proposed source as text for inline editor preview.
 * @summary Get Proposed Source
 */
export const getProposedSource = (
  { runId }: GetProposedSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRemediationProposedSource>(
    { url: `/remediation-runs/${runId}/proposed-source`, method: 'GET', signal },
    options,
  );
};

export const getGetProposedSourceQueryKey = ({ runId }: GetProposedSourcePathParameters) => {
  return ['remediation-runs', runId, 'proposed-source'] as const;
};

export const getGetProposedSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: GetProposedSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProposedSourceQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProposedSource>>> = ({ signal }) =>
    getProposedSource({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProposedSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProposedSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getProposedSource>>>;
export type GetProposedSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProposedSource<
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposedSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProposedSource>>,
          TError,
          Awaited<ReturnType<typeof getProposedSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProposedSource<
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposedSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProposedSource>>,
          TError,
          Awaited<ReturnType<typeof getProposedSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProposedSource<
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposedSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Proposed Source
 */

export function useGetProposedSource<
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: GetProposedSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProposedSourceQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Proposed Source
 */
export const prefetchGetProposedSourceQuery = async <
  TData = Awaited<ReturnType<typeof getProposedSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: GetProposedSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposedSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProposedSourceQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Proposed Source
 */
export const invalidateGetProposedSource = async (
  queryClient: QueryClient,
  { runId }: GetProposedSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProposedSourceQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * List per-member proposal metadata for a unit-scoped run.
 *
 * Empty for legacy runs that only stored the singular proposed source —
 * fall back to GET ``/remediation-runs/{run_id}/proposed-source``.
 * @summary List Run Proposed Sources
 */
export const listRunProposedSources = (
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRunProposedSourcesListResponse>(
    { url: `/remediation-runs/${runId}/proposed-sources`, method: 'GET', params, signal },
    options,
  );
};

export const getListRunProposedSourcesQueryKey = (
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
) => {
  return ['remediation-runs', runId, 'proposed-sources', ...(params ? [params] : [])] as const;
};

export const getListRunProposedSourcesQueryOptions = <
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRunProposedSourcesQueryKey({ runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRunProposedSources>>> = ({ signal }) =>
    listRunProposedSources({ runId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRunProposedSources>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRunProposedSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listRunProposedSources>>>;
export type ListRunProposedSourcesQueryError = ErrorType<TLHTTPValidationError>;

export function useListRunProposedSources<
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunProposedSourcesPathParameters,
  params: undefined | ListRunProposedSourcesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunProposedSources>>,
          TError,
          Awaited<ReturnType<typeof listRunProposedSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRunProposedSources<
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunProposedSources>>,
          TError,
          Awaited<ReturnType<typeof listRunProposedSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRunProposedSources<
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Run Proposed Sources
 */

export function useListRunProposedSources<
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListRunProposedSourcesQueryOptions({ runId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Run Proposed Sources
 */
export const prefetchListRunProposedSourcesQuery = async <
  TData = Awaited<ReturnType<typeof listRunProposedSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunProposedSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListRunProposedSourcesQueryOptions({ runId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Run Proposed Sources
 */
export const invalidateListRunProposedSources = async (
  queryClient: QueryClient,
  { runId }: ListRunProposedSourcesPathParameters,
  params?: ListRunProposedSourcesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListRunProposedSourcesQueryKey({ runId }, params) }, options);

  return queryClient;
};

/**
 * Return one unit member's proposed (and original, when captured) source as text.
 * @summary Get Run Proposed Source For Object
 */
export const getRunProposedSourceForObject = (
  { runId, objectId }: GetRunProposedSourceForObjectPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRunProposedSourceContent>(
    { url: `/remediation-runs/${runId}/proposed-sources/${objectId}`, method: 'GET', signal },
    options,
  );
};

export const getGetRunProposedSourceForObjectQueryKey = ({
  runId,
  objectId,
}: GetRunProposedSourceForObjectPathParameters) => {
  return ['remediation-runs', runId, 'proposed-sources', objectId] as const;
};

export const getGetRunProposedSourceForObjectQueryOptions = <
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, objectId }: GetRunProposedSourceForObjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRunProposedSourceForObjectQueryKey({ runId, objectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRunProposedSourceForObject>>> = ({ signal }) =>
    getRunProposedSourceForObject({ runId, objectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: runId !== null && runId !== undefined && objectId !== null && objectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetRunProposedSourceForObjectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRunProposedSourceForObject>>
>;
export type GetRunProposedSourceForObjectQueryError = ErrorType<TLHTTPValidationError>;

export function useGetRunProposedSourceForObject<
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRunProposedSourceForObjectPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
          TError,
          Awaited<ReturnType<typeof getRunProposedSourceForObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRunProposedSourceForObject<
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRunProposedSourceForObjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
          TError,
          Awaited<ReturnType<typeof getRunProposedSourceForObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRunProposedSourceForObject<
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRunProposedSourceForObjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Run Proposed Source For Object
 */

export function useGetRunProposedSourceForObject<
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, objectId }: GetRunProposedSourceForObjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRunProposedSourceForObjectQueryOptions({ runId, objectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Run Proposed Source For Object
 */
export const prefetchGetRunProposedSourceForObjectQuery = async <
  TData = Awaited<ReturnType<typeof getRunProposedSourceForObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId, objectId }: GetRunProposedSourceForObjectPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRunProposedSourceForObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRunProposedSourceForObjectQueryOptions({ runId, objectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Run Proposed Source For Object
 */
export const invalidateGetRunProposedSourceForObject = async (
  queryClient: QueryClient,
  { runId, objectId }: GetRunProposedSourceForObjectPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetRunProposedSourceForObjectQueryKey({ runId, objectId }) },
    options,
  );

  return queryClient;
};

/**
 * Re-attempt a terminal (SUCCEEDED / FAILED) agentic run.
 *
 * Creates a new run row linked to the original via ``parent_run_id`` and
 * kicks a fresh workflow. The original row is preserved as history.
 * @summary Retry Run
 */
export const retryRun = (
  { runId }: RetryRunPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRemediationRunStartedResponse>(
    { url: `/remediation-runs/${runId}/retry`, method: 'POST', signal },
    options,
  );
};

export const getRetryRunQueryKey = ({ runId }: RetryRunPathParameters) => {
  return ['POST', 'remediation-runs', runId, 'retry'] as const;
};

export const getRetryRunQueryOptions = <
  TData = Awaited<ReturnType<typeof retryRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: RetryRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRetryRunQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof retryRun>>> = ({ signal }) =>
    retryRun({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof retryRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RetryRunQueryResult = NonNullable<Awaited<ReturnType<typeof retryRun>>>;
export type RetryRunQueryError = ErrorType<TLHTTPValidationError>;

export function useRetryRun<TData = Awaited<ReturnType<typeof retryRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RetryRunPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof retryRun>>, TError, Awaited<ReturnType<typeof retryRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryRun<TData = Awaited<ReturnType<typeof retryRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RetryRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof retryRun>>, TError, Awaited<ReturnType<typeof retryRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryRun<TData = Awaited<ReturnType<typeof retryRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RetryRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retry Run
 */

export function useRetryRun<TData = Awaited<ReturnType<typeof retryRun>>, TError = ErrorType<TLHTTPValidationError>>(
  { runId }: RetryRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRetryRunQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Retry Run
 */
export const prefetchRetryRunQuery = async <
  TData = Awaited<ReturnType<typeof retryRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: RetryRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRetryRunQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Retry Run
 */
export const invalidateRetryRun = async (
  queryClient: QueryClient,
  { runId }: RetryRunPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRetryRunQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * Restore the run's compilation unit to a recorded original on SAP.
 *
 * ``source`` picks the original: the run's pre-run storage snapshot
 * (``RUN_SNAPSHOT``) or each member's program v1.0 baseline
 * (``PROGRAM_BASELINE``). Only terminal runs can be rolled back.
 * @summary Rollback Agentic Run
 */
export const rollbackAgenticRun = (
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAgenticRollbackResponse>(
    {
      url: `/remediation-runs/${runId}/rollback`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAgenticRollbackRequest,
      signal,
    },
    options,
  );
};

export const getRollbackAgenticRunQueryKey = (
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest?: BodyType<TLAgenticRollbackRequest>,
) => {
  return ['POST', 'remediation-runs', runId, 'rollback', tLAgenticRollbackRequest] as const;
};

export const getRollbackAgenticRunQueryOptions = <
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRollbackAgenticRunQueryKey({ runId }, tLAgenticRollbackRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rollbackAgenticRun>>> = ({ signal }) =>
    rollbackAgenticRun({ runId }, tLAgenticRollbackRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof rollbackAgenticRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RollbackAgenticRunQueryResult = NonNullable<Awaited<ReturnType<typeof rollbackAgenticRun>>>;
export type RollbackAgenticRunQueryError = ErrorType<TLHTTPValidationError>;

export function useRollbackAgenticRun<
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rollbackAgenticRun>>,
          TError,
          Awaited<ReturnType<typeof rollbackAgenticRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRollbackAgenticRun<
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rollbackAgenticRun>>,
          TError,
          Awaited<ReturnType<typeof rollbackAgenticRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRollbackAgenticRun<
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rollback Agentic Run
 */

export function useRollbackAgenticRun<
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRollbackAgenticRunQueryOptions({ runId }, tLAgenticRollbackRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rollback Agentic Run
 */
export const prefetchRollbackAgenticRunQuery = async <
  TData = Awaited<ReturnType<typeof rollbackAgenticRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rollbackAgenticRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRollbackAgenticRunQueryOptions({ runId }, tLAgenticRollbackRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Rollback Agentic Run
 */
export const invalidateRollbackAgenticRun = async (
  queryClient: QueryClient,
  { runId }: RollbackAgenticRunPathParameters,
  tLAgenticRollbackRequest: BodyType<TLAgenticRollbackRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRollbackAgenticRunQueryKey({ runId }, tLAgenticRollbackRequest) },
    options,
  );

  return queryClient;
};
